import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchHelpArticleBySlug, submitArticleFeedback } from '../api/GlobalApi';

function HelpArticlePage() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [article, setArticle] = useState(null);
  const [relatedArticles, setRelatedArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [feedbackSubmitted, setFeedbackSubmitted] = useState(false);

  useEffect(() => {
    loadArticle();
  }, [slug]);

  const loadArticle = async () => {
    try {
      setLoading(true);
      const response = await fetchHelpArticleBySlug(slug);
      setArticle(response.article);
      setRelatedArticles(response.relatedArticles || []);
    } catch (error) {
      console.error('Error loading article:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFeedback = async (isHelpful) => {
    try {
      await submitArticleFeedback(article.id, { is_helpful: isHelpful });
      setFeedbackSubmitted(true);
      // Update local counts
      if (isHelpful) {
        setArticle(prev => ({ ...prev, helpful_count: prev.helpful_count + 1 }));
      } else {
        setArticle(prev => ({ ...prev, not_helpful_count: prev.not_helpful_count + 1 }));
      }
    } catch (error) {
      console.error('Error submitting feedback:', error);
    }
  };

  if (loading) {
    return (
      <div style={styles.loaderContainer}>
        <div style={styles.spinner}></div>
      </div>
    );
  }

  if (!article) {
    return (
      <div style={styles.notFound}>
        <h2>Article Not Found</h2>
        <p>The article you're looking for doesn't exist.</p>
        <button onClick={() => navigate('/help')} style={styles.backBtn}>
          Back to Help Center
        </button>
      </div>
    );
  }

  return (
    <main className="main-content">
      {/* Breadcrumb */}
      <div style={styles.breadcrumb}>
        <a href="/help" style={styles.breadcrumbLink}>Help Center</a>
        <span style={styles.breadcrumbSeparator}>/</span>
        <a href={`/help/category/${article.category.slug}`} style={styles.breadcrumbLink}>
          {article.category.name}
        </a>
        <span style={styles.breadcrumbSeparator}>/</span>
        <span style={styles.breadcrumbCurrent}>{article.title}</span>
      </div>

      {/* Article Header */}
      <article style={styles.article}>
        <div style={styles.articleHeader}>
          <span style={styles.categoryBadge}>{article.category.name}</span>
          <h1 style={styles.articleTitle}>{article.title}</h1>
          {article.excerpt && (
            <p style={styles.articleExcerpt}>{article.excerpt}</p>
          )}
          <div style={styles.articleMeta}>
            <div style={styles.metaItem}>
              <svg style={styles.metaIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
              </svg>
              <span>{article.view_count} views</span>
            </div>
            <div style={styles.metaItem}>
              <svg style={styles.metaIcon} fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <span>Updated {new Date(article.updated_at).toLocaleDateString()}</span>
            </div>
          </div>
        </div>

        {/* Article Content */}
        <div 
          style={styles.articleContent}
          dangerouslySetInnerHTML={{ __html: article.content }}
        />

        {/* Feedback Section */}
        <div style={styles.feedbackSection}>
          <h3 style={styles.feedbackTitle}>Was this article helpful?</h3>
          {!feedbackSubmitted ? (
            <div style={styles.feedbackBtns}>
              <button 
                onClick={() => handleFeedback(true)} 
                style={styles.feedbackBtn}
              >
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M8.864.046C7.908-.193 7.02.53 6.956 1.466c-.072 1.051-.23 2.016-.428 2.59-.125.36-.479 1.013-1.04 1.639-.557.623-1.282 1.178-2.131 1.41C2.685 7.288 2 7.87 2 8.72v4.001c0 .845.682 1.464 1.448 1.545 1.07.114 1.564.415 2.068.723l.048.03c.272.165.578.348.97.484.397.136.861.217 1.466.217h3.5c.937 0 1.599-.477 1.934-1.064a1.86 1.86 0 0 0 .254-.912c0-.152-.023-.312-.077-.464.201-.263.38-.578.488-.901.11-.33.172-.762.004-1.149.069-.13.12-.269.159-.403.077-.27.113-.568.113-.857 0-.288-.036-.585-.113-.856a2.144 2.144 0 0 0-.138-.362 1.9 1.9 0 0 0 .234-1.734c-.206-.592-.682-1.1-1.2-1.272-.847-.282-1.803-.276-2.516-.211a9.84 9.84 0 0 0-.443.05 9.365 9.365 0 0 0-.062-4.509A1.38 1.38 0 0 0 9.125.111L8.864.046zM11.5 14.721H8c-.51 0-.863-.069-1.14-.164-.281-.097-.506-.228-.776-.393l-.04-.024c-.555-.339-1.198-.731-2.49-.868-.333-.036-.554-.29-.554-.55V8.72c0-.254.226-.543.62-.65 1.095-.3 1.977-.996 2.614-1.708.635-.71 1.064-1.475 1.238-1.978.243-.7.407-1.768.482-2.85.025-.362.36-.594.667-.518l.262.066c.16.04.258.143.288.255a8.34 8.34 0 0 1-.145 4.725.5.5 0 0 0 .595.644l.003-.001.014-.003.058-.014a8.908 8.908 0 0 1 1.036-.157c.663-.06 1.457-.054 2.11.164.175.058.45.3.57.65.107.308.087.67-.266 1.022l-.353.353.353.354c.043.043.105.141.154.315.048.167.075.37.075.581 0 .212-.027.414-.075.582-.05.174-.111.272-.154.315l-.353.353.353.354c.047.047.109.177.005.488a2.224 2.224 0 0 1-.505.805l-.353.353.353.354c.006.005.041.05.041.17a.866.866 0 0 1-.121.416c-.165.288-.503.56-1.066.56z"/>
                </svg>
                Yes ({article.helpful_count})
              </button>
              <button 
                onClick={() => handleFeedback(false)} 
                style={styles.feedbackBtn}
              >
                <svg width="20" height="20" fill="currentColor" viewBox="0 0 16 16">
                  <path d="M8.864 15.674c-.956.24-1.843-.484-1.908-1.42-.072-1.05-.23-2.015-.428-2.59-.125-.36-.479-1.012-1.04-1.638-.557-.624-1.282-1.179-2.131-1.41C2.685 8.432 2 7.85 2 7V3c0-.845.682-1.464 1.448-1.546 1.07-.113 1.564-.415 2.068-.723l.048-.029c.272-.166.578-.349.97-.484C6.931.08 7.395 0 8 0h3.5c.937 0 1.599.478 1.934 1.064.164.287.254.607.254.913 0 .152-.023.312-.077.464.201.262.38.577.488.9.11.33.172.762.004 1.15.069.13.12.268.159.403.077.27.113.567.113.856 0 .289-.036.586-.113.856-.035.12-.08.244-.138.363.394.571.418 1.2.234 1.733-.206.592-.682 1.1-1.2 1.272-.847.283-1.803.276-2.516.211a9.877 9.877 0 0 1-.443-.05 9.364 9.364 0 0 1-.062 4.51c-.138.508-.55.848-1.012.964l-.261.065zM11.5 1H8c-.51 0-.863.068-1.14.163-.281.097-.506.229-.776.393l-.04.025c-.555.338-1.198.73-2.49.868-.333.035-.554.29-.554.55V7c0 .255.226.543.62.65 1.095.3 1.977.997 2.614 1.709.635.71 1.064 1.475 1.238 1.977.243.7.407 1.768.482 2.85.025.362.36.595.667.518l.262-.065c.16-.04.258-.144.288-.255a8.34 8.34 0 0 0-.145-4.726.5.5 0 0 1 .595-.643h.003l.014.004.058.013a8.912 8.912 0 0 0 1.036.157c.663.06 1.457.054 2.11-.163.175-.059.45-.301.57-.651.107-.308.087-.67-.266-1.021L12.793 7l.353-.354c.043-.042.105-.14.154-.315.048-.167.075-.37.075-.581 0-.211-.027-.414-.075-.581-.05-.174-.111-.273-.154-.315l-.353-.354.353-.354c.047-.047.109-.176.005-.488a2.224 2.224 0 0 0-.505-.804l-.353-.354.353-.354c.006-.005.041-.05.041-.17a.866.866 0 0 0-.121-.415C12.4 1.272 12.063 1 11.5 1z"/>
                </svg>
                No ({article.not_helpful_count})
              </button>
            </div>
          ) : (
            <div style={styles.feedbackThanks}>
              <svg width="24" height="24" fill="currentColor" viewBox="0 0 16 16">
                <path d="M16 8A8 8 0 1 1 0 8a8 8 0 0 1 16 0zm-3.97-3.03a.75.75 0 0 0-1.08.022L7.477 9.417 5.384 7.323a.75.75 0 0 0-1.06 1.06L6.97 11.03a.75.75 0 0 0 1.079-.02l3.992-4.99a.75.75 0 0 0-.01-1.05z"/>
              </svg>
              <span>Thank you for your feedback!</span>
            </div>
          )}
        </div>
      </article>

      {/* Related Articles */}
      {relatedArticles.length > 0 && (
        <div style={styles.relatedSection}>
          <h2 style={styles.relatedTitle}>Related Articles</h2>
          <div style={styles.relatedGrid}>
            {relatedArticles.map((related) => (
              <a
                key={related.id}
                href={`/help/article/${related.slug}`}
                style={styles.relatedCard}
              >
                <h3 style={styles.relatedCardTitle}>{related.title}</h3>
                <p style={styles.relatedCardExcerpt}>{related.excerpt}</p>
                <span style={styles.relatedCardLink}>
                  Read more
                  <svg width="16" height="16" fill="currentColor" viewBox="0 0 16 16">
                    <path fillRule="evenodd" d="M1 8a.5.5 0 0 1 .5-.5h11.793l-3.147-3.146a.5.5 0 0 1 .708-.708l4 4a.5.5 0 0 1 0 .708l-4 4a.5.5 0 0 1-.708-.708L13.293 8.5H1.5A.5.5 0 0 1 1 8z"/>
                  </svg>
                </span>
              </a>
            ))}
          </div>
        </div>
      )}

      {/* Help CTA */}
      <div style={styles.helpCta}>
        <h3 style={styles.helpCtaTitle}>Need more help?</h3>
        <p style={styles.helpCtaText}>
          Our support team is always ready to assist you
        </p>
        <a href="/contact" style={styles.helpCtaBtn}>Contact Support</a>
      </div>
    </main>
  );
}

const styles = {
  loaderContainer: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    height: '100vh',
  },
  spinner: {
    width: '50px',
    height: '50px',
    border: '6px solid #ddd',
    borderTop: '6px solid #3498db',
    borderRadius: '50%',
    animation: 'spin 1s linear infinite',
  },
  notFound: {
    textAlign: 'center',
    padding: '80px 20px',
  },
  backBtn: {
    marginTop: '20px',
    padding: '12px 32px',
    background: '#1a73e8',
    color: 'white',
    border: 'none',
    borderRadius: '24px',
    cursor: 'pointer',
    fontSize: '16px',
    fontWeight: '500',
  },
  breadcrumb: {
    maxWidth: '800px',
    margin: '0 auto 32px',
    padding: '0 20px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    fontSize: '14px',
  },
  breadcrumbLink: {
    color: '#1a73e8',
    textDecoration: 'none',
  },
  breadcrumbSeparator: {
    color: '#5f6368',
  },
  breadcrumbCurrent: {
    color: '#5f6368',
  },
  article: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '0 20px 60px',
  },
  articleHeader: {
    marginBottom: '40px',
  },
  categoryBadge: {
    display: 'inline-block',
    padding: '6px 16px',
    fontSize: '13px',
    fontWeight: '500',
    color: '#667eea',
    backgroundColor: '#f0f2ff',
    borderRadius: '16px',
    marginBottom: '16px',
  },
  articleTitle: {
    fontSize: '40px',
    fontWeight: '600',
    color: '#202124',
    marginBottom: '16px',
    lineHeight: '1.3',
  },
  articleExcerpt: {
    fontSize: '18px',
    color: '#5f6368',
    lineHeight: '1.6',
    marginBottom: '24px',
  },
  articleMeta: {
    display: 'flex',
    gap: '24px',
    flexWrap: 'wrap',
  },
  metaItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    color: '#5f6368',
    fontSize: '14px',
  },
  metaIcon: {
    width: '18px',
    height: '18px',
  },
  articleContent: {
    fontSize: '17px',
    lineHeight: '1.8',
    color: '#202124',
    marginBottom: '60px',
  },
  feedbackSection: {
    padding: '40px',
    background: '#f8f9fa',
    borderRadius: '12px',
    textAlign: 'center',
    marginBottom: '60px',
  },
  feedbackTitle: {
    fontSize: '20px',
    fontWeight: '500',
    color: '#202124',
    marginBottom: '20px',
  },
  feedbackBtns: {
    display: 'flex',
    gap: '16px',
    justifyContent: 'center',
  },
  feedbackBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '12px 24px',
    background: 'white',
    border: '1px solid #dadce0',
    borderRadius: '24px',
    color: '#202124',
    fontSize: '15px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  feedbackThanks: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '12px',
    color: '#1e8e3e',
    fontSize: '16px',
    fontWeight: '500',
  },
  relatedSection: {
    maxWidth: '1200px',
    margin: '0 auto 60px',
    padding: '0 20px',
  },
  relatedTitle: {
    fontSize: '28px',
    fontWeight: '600',
    color: '#202124',
    marginBottom: '24px',
  },
  relatedGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
    gap: '24px',
  },
  relatedCard: {
    padding: '24px',
    background: 'white',
    border: '1px solid #e8eaed',
    borderRadius: '12px',
    textDecoration: 'none',
    transition: 'all 0.2s',
    display: 'flex',
    flexDirection: 'column',
  },
  relatedCardTitle: {
    fontSize: '18px',
    fontWeight: '500',
    color: '#202124',
    marginBottom: '8px',
  },
  relatedCardExcerpt: {
    fontSize: '14px',
    color: '#5f6368',
    lineHeight: '1.5',
    marginBottom: '12px',
    flex: 1,
  },
  relatedCardLink: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    color: '#1a73e8',
    fontSize: '14px',
    fontWeight: '500',
  },
  helpCta: {
    maxWidth: '800px',
    margin: '0 auto',
    padding: '0 20px',
    textAlign: 'center',
  },
  helpCtaTitle: {
    fontSize: '24px',
    fontWeight: '600',
    color: '#202124',
    marginBottom: '12px',
  },
  helpCtaText: {
    fontSize: '16px',
    color: '#5f6368',
    marginBottom: '24px',
  },
  helpCtaBtn: {
    display: 'inline-block',
    padding: '14px 36px',
    background: '#1a73e8',
    color: 'white',
    borderRadius: '24px',
    textDecoration: 'none',
    fontSize: '16px',
    fontWeight: '500',
    transition: 'all 0.2s',
  },
};

const styleSheet = document.createElement('style');
styleSheet.innerHTML = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }

  /* Article Content Styles */
  .articleContent h2 {
    font-size: 28px;
    font-weight: 600;
    color: #202124;
    margin: 32px 0 16px;
  }

  .articleContent h3 {
    font-size: 22px;
    font-weight: 600;
    color: #202124;
    margin: 24px 0 12px;
  }

  .articleContent p {
    margin-bottom: 16px;
  }

  .articleContent ul,
  .articleContent ol {
    margin: 16px 0;
    padding-left: 24px;
  }

  .articleContent li {
    margin-bottom: 8px;
  }

  .articleContent code {
    background: #f8f9fa;
    padding: 2px 6px;
    border-radius: 4px;
    font-family: monospace;
    font-size: 15px;
  }

  .articleContent pre {
    background: #f8f9fa;
    padding: 16px;
    border-radius: 8px;
    overflow-x: auto;
    margin: 16px 0;
  }

  @media (hover: hover) {
    .feedbackBtn:hover {
      background: #f8f9fa;
      border-color: #1a73e8;
    }

    .relatedCard:hover {
      box-shadow: 0 4px 12px rgba(0,0,0,0.08);
      border-color: #1a73e8;
    }

    .helpCtaBtn:hover {
      background: #1557b0;
    }
  }

  @media (max-width: 768px) {
    .articleTitle {
      font-size: 32px !important;
    }

    .feedbackBtns {
      flex-direction: column;
    }
  }
`;
document.head.appendChild(styleSheet);

export default HelpArticlePage;